import type { MarginVerdict } from '../analytics/analytics-economics';

/**
 * Verdict → display label / tone maps for the margin-adequacy KPI. Shared by the
 * KpiStrip and the condensed StickySummary so the pinned bar can never disagree
 * with the strip it condenses — one source for both. Kept in a non-component
 * module so the consuming component files export only components (react-refresh).
 */
export const MARGIN_LABEL: Record<MarginVerdict, string> = {
  no_mark: 'No mark',
  cannot_assess: 'Cannot assess',
  excess: 'Excess',
  breach: 'Shortfall',
};

/**
 * Verdict word color. cannot_assess is normally rendered as its own warn chip by
 * the callers, so it does not usually reach the fall-through — but it is handled
 * explicitly here so the function is self-protecting if a caller forgets.
 *
 * M1: an "Excess" while marked exposure already tops posted collateral is NOT
 * painted green — BUT only LEND-side (LR-208 r6 M5). Lend-side we RECEIVE
 * collateral, so loaned value topping it is our mark-to-market risk and a green
 * "Excess" would mask it. Borrow-side WE post the collateral, so exposure topping
 * it is not counterparty risk — a borrow-side excess stays green, matching
 * AdequacyCard which gates its amber concern on `exposureExceedsCollateral &&
 * isLendSide`. Without the side check the KPI strip painted a borrow-side excess
 * neutral-gray while AdequacyCard painted it green on the same page.
 */
export function verdictTextClass(
  verdict: MarginVerdict,
  exposureExceedsCollateral: boolean,
  isLendSide: boolean,
): string {
  if (verdict === 'breach') return 'text-lr-neg';
  if (verdict === 'excess') {
    return exposureExceedsCollateral && isLendSide ? 'text-lr-fg' : 'text-lr-pos';
  }
  if (verdict === 'cannot_assess') return 'text-lr-warn';
  return 'text-lr-muted'; // no_mark
}
